/**
 * SpecificCharacterSet (0008,0005) handling for the character-set-aware
 * string VRs, per PS3.3 section C.12.1.1.2 and PS3.5 section 6.1.2.
 *
 * Character set handling is explicit here rather than implied by whatever a
 * default TextDecoder happens to do: a name mangled on read is a patient name
 * mangled on screen, and one mangled on write is a patient name mangled in
 * the archive. Both directions are therefore stated, and an encoding this
 * module cannot perform faithfully raises rather than substituting
 * replacement characters.
 */

/** The character set a dataset's values are encoded in. */
export interface Charset {
    /** The declared SpecificCharacterSet value, '' when the attribute is absent. */
    readonly declared: string;
    /** The TextDecoder label for the declared set. */
    readonly decoderLabel: string;
}

/**
 * DICOM defined term to TextDecoder label, for the single-value (non
 * code-extension) terms.
 *
 * The default repertoire — an absent or empty SpecificCharacterSet, or the
 * explicit 'ISO_IR 6' — is ISO 646 / ASCII. It is decoded as windows-1252
 * rather than as strict ASCII so that a file which declares the default
 * repertoire but carries high bytes anyway (common from older planning
 * systems) yields readable text instead of replacement characters, while
 * every byte below 0x80 decodes identically to ASCII.
 */
const DECODER_LABELS: Readonly<Record<string, string>> = {
    '': 'windows-1252',
    'ISO_IR 6': 'windows-1252',
    'ISO_IR 100': 'iso-8859-1',
    'ISO_IR 101': 'iso-8859-2',
    'ISO_IR 109': 'iso-8859-3',
    'ISO_IR 110': 'iso-8859-4',
    'ISO_IR 144': 'iso-8859-5',
    'ISO_IR 127': 'iso-8859-6',
    'ISO_IR 126': 'iso-8859-7',
    'ISO_IR 138': 'iso-8859-8',
    'ISO_IR 148': 'iso-8859-9',
    'ISO_IR 166': 'iso-8859-11',
    'ISO_IR 13': 'shift_jis',
    'ISO_IR 192': 'utf-8',
    GB18030: 'gb18030',
    GBK: 'gbk',
    'ISO 2022 IR 6': 'windows-1252',
    'ISO 2022 IR 100': 'iso-8859-1',
    'ISO 2022 IR 101': 'iso-8859-2',
    'ISO 2022 IR 109': 'iso-8859-3',
    'ISO 2022 IR 110': 'iso-8859-4',
    'ISO 2022 IR 144': 'iso-8859-5',
    'ISO 2022 IR 127': 'iso-8859-6',
    'ISO 2022 IR 126': 'iso-8859-7',
    'ISO 2022 IR 138': 'iso-8859-8',
    'ISO 2022 IR 148': 'iso-8859-9',
    'ISO 2022 IR 13': 'shift_jis',
    'ISO 2022 IR 87': 'iso-2022-jp',
    'ISO 2022 IR 159': 'iso-2022-jp',
    'ISO 2022 IR 149': 'euc-kr',
    'ISO 2022 IR 58': 'gb18030',
};

/** The defined term for UTF-8, the set that can carry any text. */
export const UTF8_CHARACTER_SET = 'ISO_IR 192';

/** The default repertoire, used when (0008,0005) is absent. */
export const DEFAULT_CHARSET: Charset = {
    declared: '',
    decoderLabel: 'windows-1252',
};

/**
 * Builds the Charset for a declared SpecificCharacterSet value.
 *
 * A multi-valued declaration (code extensions, backslash-delimited) is
 * represented by its first non-empty component. Escape-sequence switching
 * between the declared sets is not performed: values are decoded wholly in
 * that component's set. The declared string is retained verbatim so a
 * dataset that is written back unmodified still carries what it arrived with.
 */
export function charsetFromDeclaration(declared: string | undefined): Charset {
    if (declared === undefined) {
        return DEFAULT_CHARSET;
    }
    const components = declared
        .split('\\')
        .map((component) => component.trim())
        .filter((component) => component.length > 0);
    // A code-extension declaration leads with an empty component meaning "the
    // default repertoire is also active"; the set that names the encoding is
    // the first one actually given.
    const primary = components[0] ?? '';
    return {
        declared,
        decoderLabel: DECODER_LABELS[primary] ?? DEFAULT_CHARSET.decoderLabel,
    };
}

const decoderCache = new Map<string, TextDecoder>();

function decoderFor(label: string): TextDecoder {
    let decoder = decoderCache.get(label);
    if (!decoder) {
        try {
            decoder = new TextDecoder(label);
        } catch {
            // A runtime without this encoding would otherwise throw deep
            // inside a parse. Falling back keeps the dataset readable, and
            // the substitution is visible because it is the only place a
            // declared label is not honoured.
            decoder = new TextDecoder(DEFAULT_CHARSET.decoderLabel);
        }
        decoderCache.set(label, decoder);
    }
    return decoder;
}

/** ASCII decoding for the string VRs restricted to the default repertoire. */
export function decodeDefaultRepertoire(bytes: Uint8Array): string {
    return decoderFor(DEFAULT_CHARSET.decoderLabel).decode(bytes);
}

/** Decodes bytes of a character-set-aware string VR in the dataset's charset. */
export function decodeText(bytes: Uint8Array, charset: Charset): string {
    return decoderFor(charset.decoderLabel).decode(bytes);
}

const utf8Encoder = new TextEncoder();

const encodingCache = new Map<string, ReadonlyMap<number, number>>();

/**
 * The characters a set encodes in a single byte, each mapped to that byte.
 *
 * Built by inverting the set's own decoder rather than from a table written
 * out here, which is what makes writing the exact inverse of reading: a value
 * decoded from a file and written back unchanged produces the byte it came
 * from, for every set, with no second table to fall out of step with the
 * first.
 *
 * A byte that decodes to nothing or to the replacement character is not in
 * the set — the holes in ISO-8859-3, and the lead bytes of the multi-byte
 * sets, which decode to a replacement character on their own. So a
 * multi-byte set yields exactly the part of itself this engine can write:
 * ASCII, plus whatever else the set gives a single byte to. The rest of such
 * a set is not synthesised here, because a lead byte written as if it were a
 * character makes the following byte part of a different character.
 */
function singleByteEncodingFor(label: string): ReadonlyMap<number, number> {
    const cached = encodingCache.get(label);
    if (cached) {
        return cached;
    }

    const decoder = decoderFor(label);
    const table = new Map<number, number>();
    const one = new Uint8Array(1);
    for (let byte = 0; byte <= 0xff; byte++) {
        one[0] = byte;
        const decoded = decoder.decode(one);
        if (decoded.length !== 1) {
            continue;
        }
        const code = decoded.codePointAt(0) ?? 0;
        if (code === REPLACEMENT_CHARACTER) {
            continue;
        }
        // Ascending, first byte wins, so the mapping is the same on every run
        // and on every platform whose decoder agrees.
        if (!table.has(code)) {
            table.set(code, byte);
        }
    }

    encodingCache.set(label, table);
    return table;
}

const REPLACEMENT_CHARACTER = 0xfffd;

/**
 * Whether a character set can carry every character of `value` unchanged.
 *
 * Asked before writing rather than inferred afterwards, so a caller can refuse
 * to narrow a value instead of discovering that it did.
 *
 * Answered from the set itself. Judging instead by whether a character's code
 * point fits in a byte answers a question about Latin-1 whatever set was
 * declared, and gets it wrong in both directions: a Cyrillic name under
 * ISO_IR 144 is reported unrepresentable though the set holds it, while a
 * Latin letter under ISO_IR 126 is reported representable and then written as
 * the Greek letter sharing its byte.
 */
export function canRepresent(value: string, charset: Charset): boolean {
    if (charset.decoderLabel === 'utf-8') {
        return true;
    }
    const encoding = singleByteEncodingFor(charset.decoderLabel);
    return [...value].every((character) =>
        encoding.has(character.codePointAt(0) ?? 0)
    );
}

/** A value that had to be changed to fit the character set being written. */
export interface CharacterSubstitution {
    /** The text as the user or the source file had it. */
    readonly original: string;
    /** The text actually encoded. */
    readonly written: string;
    /** The declared character set the text had to fit. */
    readonly charset: string;
}

/**
 * Collects the substitutions made while encoding a dataset.
 *
 * Reducing text to fit a character set loses information, so it is never done
 * silently: the caller is handed what changed, so it can be shown to the user
 * and recorded. A writer that quietly altered a structure name would leave the
 * user acting on a name the archive does not hold.
 */
export class SubstitutionLog {
    private readonly entries: CharacterSubstitution[] = [];

    record(entry: CharacterSubstitution): void {
        this.entries.push(entry);
    }

    get substitutions(): readonly CharacterSubstitution[] {
        return this.entries;
    }

    get isEmpty(): boolean {
        return this.entries.length === 0;
    }
}

/**
 * Encodes text for a character-set-aware string VR, reducing it to characters
 * the declared set can represent where that set cannot hold the original.
 *
 * Transferring the object matters more than transferring every glyph: a file
 * that fails to export is of no use to the receiving system, while one whose
 * text has lost its diacritics still identifies the same patient and carries
 * the same study. So an unrepresentable character is reduced, not refused.
 *
 * The reduction is reported through `log` rather than performed silently — see
 * {@link SubstitutionLog}. A dataset read and written without editing its text
 * never reaches here at all, because unmodified elements are re-emitted from
 * their source bytes.
 */
export function encodeText(
    value: string,
    charset: Charset,
    log?: SubstitutionLog
): Uint8Array {
    if (charset.decoderLabel === 'utf-8') {
        // UTF-8 represents every character, so nothing is ever reduced.
        return utf8Encoder.encode(value);
    }
    return encodeSingleByte(value, charset, log);
}

/**
 * Reduces a string to the characters a single-byte set can hold.
 *
 * Decomposing first and dropping the combining marks is what makes the result
 * readable rather than merely transferable: "jyvät" reduces to "jyvat" instead
 * of "jyv?t", so a Nordic or Central European name still names the same
 * person. Only a character with no usable decomposition falls back to a
 * placeholder.
 */
function reduceToRepresentable(
    value: string,
    canEncode: (code: number) => boolean
): string {
    let reduced = '';
    for (const character of value) {
        const code = character.codePointAt(0) ?? 0;
        if (canEncode(code)) {
            reduced += character;
            continue;
        }
        // NFD splits an accented letter into its base letter plus combining
        // marks; dropping the marks leaves the base letter behind.
        const stripped = character
            .normalize('NFD')
            .replace(/\p{Diacritic}/gu, '');
        const usable =
            stripped.length > 0 &&
            [...stripped].every((part) => canEncode(part.codePointAt(0) ?? 0));
        reduced += usable ? stripped : '?';
    }
    return reduced;
}

function encodeSingleByte(
    value: string,
    charset: Charset,
    log?: SubstitutionLog
): Uint8Array {
    const encoding = singleByteEncodingFor(charset.decoderLabel);
    const canEncode = (code: number): boolean => encoding.has(code);
    const representable = [...value].every((character) =>
        canEncode(character.codePointAt(0) ?? 0)
    );
    const written = representable
        ? value
        : reduceToRepresentable(value, canEncode);

    if (written !== value) {
        log?.record({
            original: value,
            written,
            charset: charset.declared || 'ISO_IR 6 (default repertoire)',
        });
    }

    // By code point rather than by code unit: reduction can lengthen a value,
    // and a character outside the Basic Multilingual Plane is two code units
    // that are one character.
    const characters = [...written];
    const bytes = new Uint8Array(characters.length);
    for (let i = 0; i < characters.length; i++) {
        const byte = encoding.get(characters[i].codePointAt(0) ?? 0);
        if (byte === undefined) {
            // The reduction leaves only characters this set encodes, so the
            // two disagree. Emitting a byte anyway is how a value comes to
            // say something other than what was reduced and reported.
            throw new Error(
                `Character '${characters[i]}' survived reduction for ` +
                    `${charset.declared || 'the default repertoire'} but the ` +
                    'set does not encode it.'
            );
        }
        bytes[i] = byte;
    }
    return bytes;
}

/**
 * Encodes a string VR restricted to the default repertoire.
 *
 * These VRs carry UIDs, dates, codes and the like, which are ASCII by
 * definition; anything outside it is reduced the same way.
 */
export function encodeDefaultRepertoire(
    value: string,
    log?: SubstitutionLog
): Uint8Array {
    return encodeSingleByte(value, DEFAULT_CHARSET, log);
}
